// Shared data source for tasks and document review exceptions
// This ensures both TasksManagement and DIExceptionHandling use the same data

export interface ExceptionDocument {
  id: string
  documentId: string
  documentType: string
  detectedType: string | null
  vendor: string
  date: string
  amount: number
  status: 'New' | 'In Progress' | 'Waiting Approval' | 'Completed' | 'Cancelled'
  reason: string
  createdAt: string
  assignedTo: string | null
  confidence: number
  pages: Array<{
    id: string
    thumbnail: string
    fullImage: string
  }>
}

export interface Task {
  id: string
  title: string
  description: string
  module:
    | 'PO'
    | 'VM'
    | 'AP'
    | 'CM'
    | 'EM'
    | 'DI'
    | 'OCR'
    | 'Financial'
    | 'Settings'
  status:
    | 'Pending'
    | 'In Progress'
    | 'Completed'
    | 'Cancelled'
    | 'Overdue'
    | 'Waiting Approval'
  priority: 'Low' | 'Medium' | 'High' | 'Urgent'
  dueDate: string
  assignedTo: string
  assignedToType?: 'user' | 'group' // New field to distinguish between user and group
  assignedBy: string
  createdAt: string
  updatedAt?: string
  completedAt?: string
  relatedEntityId?: string
  relatedEntityType?: string
  relatedEntityName?: string
  tags?: string[]
  taskType: 'Review & Confirm' | 'Action Required' | 'Document Review'
  exceptionDocumentId?: string // Link to exception document if it's a document review task
}

// Sample exception documents - this is the source of truth
export const exceptionDocuments: ExceptionDocument[] = [
  {
    id: 'exc-001',
    documentId: 'doc-003',
    documentType: 'Invoice',
    detectedType: 'PO Invoice',
    vendor: 'PrintWorks Inc.',
    date: '2023-10-05',
    amount: 3200.5,
    status: 'New',
    reason: 'PO Number Not Found',
    createdAt: '2023-10-05T14:30:00Z',
    assignedTo: null,
    confidence: 0.65,
    pages: [
      {
        id: 'page-001',
        thumbnail:
          'https://templates.invoicehome.com/invoice-template-us-neat-750px.png',
        fullImage:
          'https://templates.invoicehome.com/invoice-template-us-neat-750px.png',
      },
      {
        id: 'page-002',
        thumbnail:
          'https://templates.invoicehome.com/invoice-template-us-classic-blue-750px.png',
        fullImage:
          'https://templates.invoicehome.com/invoice-template-us-classic-blue-750px.png',
      },
    ],
  },
  {
    id: 'exc-002',
    documentId: 'doc-007',
    documentType: 'Credit Note',
    detectedType: 'Credit Note',
    vendor: 'Tech Solutions',
    date: '2023-10-02',
    amount: 1500.0,
    status: 'In Progress',
    reason: 'Missing Invoice Reference',
    createdAt: '2023-10-02T09:15:00Z',
    assignedTo: 'john.doe@example.com',
    confidence: 0.72,
    pages: [
      {
        id: 'page-003',
        thumbnail:
          'https://templates.invoicehome.com/credit-note-template-us-neat-750px.png',
        fullImage:
          'https://templates.invoicehome.com/credit-note-template-us-neat-750px.png',
      },
    ],
  },
  {
    id: 'exc-003',
    documentId: 'doc-012',
    documentType: 'Unknown',
    detectedType: null,
    vendor: 'Office Supplies Co.',
    date: '2023-09-28',
    amount: 456.75,
    status: 'New',
    reason: 'Classification Failed',
    createdAt: '2023-09-28T16:45:00Z',
    assignedTo: null,
    confidence: 0.35,
    pages: [
      {
        id: 'page-004',
        thumbnail:
          'https://templates.invoicehome.com/receipt-template-us-neat-750px.png',
        fullImage:
          'https://templates.invoicehome.com/receipt-template-us-neat-750px.png',
      },
      {
        id: 'page-005',
        thumbnail:
          'https://templates.invoicehome.com/receipt-template-us-classic-blue-750px.png',
        fullImage:
          'https://templates.invoicehome.com/receipt-template-us-classic-blue-750px.png',
      },
      {
        id: 'page-006',
        thumbnail:
          'https://templates.invoicehome.com/receipt-template-us-band-750px.png',
        fullImage:
          'https://templates.invoicehome.com/receipt-template-us-band-750px.png',
      },
    ],
  },
  {
    id: 'exc-004',
    documentId: 'doc-015',
    documentType: 'Invoice',
    detectedType: 'Non-PO Invoice',
    vendor: 'Global Services Ltd.',
    date: '2023-09-25',
    amount: 2750.0,
    status: 'Waiting Approval',
    reason: 'GL Code Missing',
    createdAt: '2023-09-25T11:20:00Z',
    assignedTo: 'sarah.smith@example.com',
    confidence: 0.58,
    pages: [
      {
        id: 'page-007',
        thumbnail:
          'https://templates.invoicehome.com/invoice-template-us-modern-red-750px.png',
        fullImage:
          'https://templates.invoicehome.com/invoice-template-us-modern-red-750px.png',
      },
    ],
  },
  {
    id: 'exc-005',
    documentId: 'doc-018',
    documentType: 'Invoice',
    detectedType: 'PO Invoice',
    vendor: 'Hardware Supplies Inc.',
    date: '2023-09-22',
    amount: 1850.25,
    status: 'New',
    reason: 'Line Item Mismatch',
    createdAt: '2023-09-22T13:40:00Z',
    assignedTo: null,
    confidence: 0.78,
    pages: [
      {
        id: 'page-008',
        thumbnail:
          'https://templates.invoicehome.com/invoice-template-us-classic-white-750px.png',
        fullImage:
          'https://templates.invoicehome.com/invoice-template-us-classic-white-750px.png',
      },
      {
        id: 'page-009',
        thumbnail:
          'https://templates.invoicehome.com/invoice-template-us-modern-orange-750px.png',
        fullImage:
          'https://templates.invoicehome.com/invoice-template-us-modern-orange-750px.png',
      },
    ],
  },
]

// Convert exception documents to tasks
export function convertExceptionToTask(exception: ExceptionDocument): Task {
  // Calculate due date (3 days from creation for urgent, 7 days for others)
  const createdDate = new Date(exception.createdAt)
  const dueDate = new Date(createdDate)
  const isUrgent =
    exception.confidence < 0.6 || exception.reason.includes('Failed')
  dueDate.setDate(createdDate.getDate() + (isUrgent ? 3 : 7))

  // Determine if overdue
  const now = new Date()
  const status =
    exception.status === 'Completed'
      ? 'Completed'
      : exception.status === 'Cancelled'
        ? 'Cancelled'
        : exception.status === 'Waiting Approval'
          ? 'Waiting Approval'
          : exception.status === 'In Progress'
            ? 'In Progress'
            : dueDate < now
              ? 'Overdue'
              : 'Pending'

  // Determine priority based on confidence and reason
  let priority: Task['priority'] = 'Medium'
  if (exception.confidence < 0.5 || exception.reason.includes('Failed')) {
    priority = 'Urgent'
  } else if (
    exception.confidence < 0.7 ||
    exception.reason.includes('Not Found') ||
    exception.reason.includes('Missing')
  ) {
    priority = 'High'
  }

  return {
    id: `task-${exception.id}`,
    title: `Review ${exception.documentType || 'Document'}: ${exception.vendor}`,
    description: `${exception.reason} - ${exception.detectedType || 'Unknown type'} document requires review and correction`,
    module: 'DI',
    status,
    priority,
    dueDate: dueDate.toISOString(),
    assignedTo: exception.assignedTo || 'Admin',
    assignedToType: 'user',
    assignedBy: 'DI System',
    createdAt: exception.createdAt,
    updatedAt:
      exception.status !== 'New' ? new Date().toISOString() : undefined,
    completedAt:
      exception.status === 'Completed' ? new Date().toISOString() : undefined,
    relatedEntityId: exception.documentId,
    relatedEntityType: 'Document',
    relatedEntityName: `${exception.vendor} - ${exception.documentType}`,
    tags: [
      'document-review',
      'exception',
      exception.documentType.toLowerCase(),
    ],
    taskType: 'Document Review',
    exceptionDocumentId: exception.id,
  }
}

// Other task types (non-document review)
export const otherTasks: Task[] = [
  {
    id: 'task-001',
    title: 'Review Purchase Order #PO-2023-005',
    description: 'Review and approve the purchase order for office supplies',
    module: 'PO',
    status: 'Pending',
    priority: 'Medium',
    dueDate: '2023-10-30T17:00:00Z',
    assignedTo: 'Admin',
    assignedToType: 'user',
    assignedBy: 'John Smith',
    createdAt: '2023-10-20T09:30:00Z',
    relatedEntityId: 'PO-2023-005',
    relatedEntityType: 'PurchaseOrder',
    relatedEntityName: 'Office Supplies Order',
    tags: ['review', 'approval', 'supplies'],
    taskType: 'Review & Confirm',
  },
  {
    id: 'task-002',
    title: 'Validate Vendor Information',
    description:
      'Verify new vendor compliance documents and contact information',
    module: 'VM',
    status: 'In Progress',
    priority: 'High',
    dueDate: '2023-10-25T17:00:00Z',
    assignedTo: 'Procurement Team',
    assignedToType: 'group',
    assignedBy: 'Sarah Johnson',
    createdAt: '2023-10-18T14:15:00Z',
    updatedAt: '2023-10-19T10:20:00Z',
    relatedEntityId: 'VENDOR-2023-042',
    relatedEntityType: 'Vendor',
    relatedEntityName: 'Acme Supplies Inc.',
    tags: ['verification', 'compliance', 'onboarding'],
    taskType: 'Action Required',
  },
  {
    id: 'task-003',
    title: 'Run Monthly Financial Report',
    description:
      'Generate and review the monthly financial summary report for October 2023. Confirm completion once the report has been run and distributed to stakeholders.',
    module: 'Financial',
    status: 'Pending',
    priority: 'High',
    dueDate: '2023-11-05T17:00:00Z',
    assignedTo: 'Mandar Kulkarni',
    assignedToType: 'user',
    assignedBy: 'Admin',
    createdAt: '2023-10-25T08:00:00Z',
    relatedEntityId: 'REPORT-2023-10',
    relatedEntityType: 'Report',
    relatedEntityName: 'Monthly Financial Summary - October 2023',
    tags: ['report', 'monthly', 'financial'],
    taskType: 'Action Required',
  },
  {
    id: 'task-004',
    title: 'Update Contract Milestone',
    description:
      'Update the milestone completion status for Project Alpha contract',
    module: 'CM',
    status: 'Completed',
    priority: 'Medium',
    dueDate: '2023-10-22T17:00:00Z',
    assignedTo: 'Admin',
    assignedToType: 'user',
    assignedBy: 'Jennifer Lee',
    createdAt: '2023-10-19T09:00:00Z',
    completedAt: '2023-10-21T16:30:00Z',
    relatedEntityId: 'CONTRACT-2023-015',
    relatedEntityType: 'Contract',
    relatedEntityName: 'Project Alpha Development',
    tags: ['milestone', 'update', 'contract'],
    taskType: 'Action Required',
  },
  {
    id: 'task-005',
    title: 'Approve Expense Claim',
    description: 'Review and approve expense claim for business trip',
    module: 'EM',
    status: 'Pending',
    priority: 'Low',
    dueDate: '2023-11-05T17:00:00Z',
    assignedTo: 'Finance Team',
    assignedToType: 'group',
    assignedBy: 'David Wilson',
    createdAt: '2023-10-22T10:15:00Z',
    relatedEntityId: 'EXP-2023-056',
    relatedEntityType: 'ExpenseClaim',
    relatedEntityName: 'Business Trip - New York',
    tags: ['approval', 'expense', 'travel'],
    taskType: 'Review & Confirm',
  },
  {
    id: 'task-007',
    title: 'Configure Budget Alerts',
    description: 'Set up budget threshold alerts for Q4 departmental budgets',
    module: 'Financial',
    status: 'Pending',
    priority: 'Medium',
    dueDate: '2023-10-31T17:00:00Z',
    assignedTo: 'Admin',
    assignedToType: 'user',
    assignedBy: 'Robert Taylor',
    createdAt: '2023-10-24T11:30:00Z',
    relatedEntityId: 'BUDGET-2023-Q4',
    relatedEntityType: 'Budget',
    relatedEntityName: 'Q4 2023 Departmental Budgets',
    tags: ['configuration', 'budget', 'alerts'],
    taskType: 'Action Required',
  },
]

// Get all tasks (document review + other tasks)
export function getAllTasks(): Task[] {
  const documentReviewTasks = exceptionDocuments.map(convertExceptionToTask)
  return [...documentReviewTasks, ...otherTasks]
}
